import { useRef, useState, type DragEvent } from "react";

/**
 * Arrastrar y soltar archivos sobre un elemento.
 *
 * Es un hook y no un componente porque las dos zonas que lo usan se ven
 * distinto —la caja grande del modal "Añadir fuente" y las franjas del paso de
 * sistema de diseño— y lo único que comparten es LO QUE NO PUEDE FALLAR:
 *
 *  · `dragover` lleva `preventDefault()` sí o sí. Sin eso el navegador rechaza
 *    el soltado y, peor, si el archivo cae fuera de la zona la pestaña NAVEGA
 *    al archivo y el usuario pierde la pantalla entera.
 *  · `dragleave` dispara al pasar sobre los hijos, así que el resaltado se
 *    apaga con un CONTADOR de entradas y no con un booleano: con un booleano,
 *    mover el mouse por adentro lo apaga a mitad del gesto.
 *
 * Devuelve el flag `encima` (para pintar el resaltado) y los cuatro handlers
 * listos para desparramar sobre el elemento que recibe el soltado.
 *
 * Ver ADR-2026-08-17-estudio-marketing-paso-sistema-diseno.
 */

export interface SoltarArchivos {
  /** Hay un arrastre encima de la zona ahora mismo. */
  encima: boolean;
  props: {
    onDragEnter: (evento: DragEvent<HTMLElement>) => void;
    onDragOver: (evento: DragEvent<HTMLElement>) => void;
    onDragLeave: (evento: DragEvent<HTMLElement>) => void;
    onDrop: (evento: DragEvent<HTMLElement>) => void;
  };
}

export function useSoltarArchivos(onArchivos: (archivos: File[]) => void): SoltarArchivos {
  const [encima, setEncima] = useState(false);
  const profundidad = useRef(0);

  return {
    encima,
    props: {
      onDragEnter: (evento) => {
        evento.preventDefault();
        profundidad.current += 1;
        setEncima(true);
      },
      onDragOver: (evento) => evento.preventDefault(),
      onDragLeave: (evento) => {
        evento.preventDefault();
        profundidad.current -= 1;
        if (profundidad.current <= 0) setEncima(false);
      },
      onDrop: (evento) => {
        evento.preventDefault();
        profundidad.current = 0;
        setEncima(false);
        onArchivos(Array.from(evento.dataTransfer.files ?? []));
      },
    },
  };
}
